import { DrawingLayer } from 'business/models/DrawingLayer';
import React, { useEffect, useState, useCallback } from 'react';
import { Group } from 'react-konva';

import { RMRectangle, TextProps } from 'business/models/CustomModels';
import {
  GetDisplayMode,
  DisplayModes,
  GetUIRect,
  GetUIText,
  isNullOrUndefined,
  showSnackBar,
} from 'utils/Global';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import { GridLayerDTO, LayerDTO, LayerTypeDTO } from 'business/models/Models';
import {
  findErrorsInLayer,
  GetWellNum,
} from 'business/plateviewer/Generate/GenerateCommon';
import ErrorIcon from 'Icons/Error.svg';
import WarningIcon from 'Icons/Warning.svg';
import { selectDeletedLayers } from 'redux/selectors/StateSelectors';
import Konva from 'konva';
import { selectExp, selectSelectedLayer } from 'redux/selectors/ExperimentStateSelector';
import { updateLayersCanvasData } from 'redux/reducers/experimentDataReducer';

const layerColor = 'rgb(160,160,160)';
const layerErrorColor = 'red';

const LayerRenderer: React.FC<{
  canvasRefs: any;
  drawingLayer: Konva.Layer;
  errorLayer: React.MutableRefObject<Konva.Layer>;
  errorLayerIconRef: React.MutableRefObject<Konva.Layer>;
  selectionColor;
  strokeNormalizer: number;
  setLayerErrorIcon: React.Dispatch<React.SetStateAction<any>>;
}> = ({
  canvasRefs,
  drawingLayer,
  errorLayer,
  errorLayerIconRef,
  selectionColor,
  strokeNormalizer,
  setLayerErrorIcon,
}) => {
  const _selectedLayer = useRMSelector(selectSelectedLayer);
  const deletedLayers = useRMSelector(selectDeletedLayers);
  const [drawableLayers, setDrawableLayers] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const dispatch = useRMDispatch();
  const exp = useRMSelector(selectExp);
  const setLayersData = useCallback(
    (layers: LayerDTO[]) => dispatch(updateLayersCanvasData(layers)),
    [dispatch],
  );
  function isDesignVisible() {
    return GetDisplayMode() === DisplayModes.DESIGN;
  }

  useEffect(() => {
    const lyrs = exp.layers.filter(
      (l) => !deletedLayers.find((dl) => !isNullOrUndefined(dl) && dl.id === l.id),
    );
    // Prevent unecessary redux update if layer rects are not changed
    const updateRedux = lyrs.some((l) => l.dirty || !l.tabRect || !l.layerRect);
    drawLayers(lyrs);
    generateUILayers(lyrs);
    if (updateRedux) {
      setLayersData(lyrs);
    }
  }, [exp.layers, exp.plateDto, deletedLayers, errorLayer]);

  useEffect(() => {
    if (!canvasRefs) {
      return;
    }
  }, [canvasRefs]);

  // need to call this even if layer rects are calculated previously bcz plate.wellRects change after resize (needs to improve later)
  // by passing a prop to layerrenderer only in case of wellRects changed
  function clearErrorLayer() {
    if (isNullOrUndefined(errorLayer) || isNullOrUndefined(errorLayer.current)) {
      return;
    }
    errorLayer.current.destroyChildren();
    errorLayer.current.clear();
    errorLayer.current.batchDraw();
    clearErrorLayerIcon();
  }
  function clearErrorLayerIcon() {
    if (
      isNullOrUndefined(errorLayerIconRef) ||
      isNullOrUndefined(errorLayerIconRef.current)
    ) {
      return;
    }
    errorLayerIconRef.current.destroyChildren();
    errorLayerIconRef.current.clear();
    errorLayerIconRef.current.batchDraw();
  }
  function drawLayers(layers: Array<DrawingLayer>) {
    clearErrorLayer();
    let tempErrorIcon = null;
    if (!layers || layers.length === 0) {
      return null;
    }

    const lRects = [];
    const _tabRects = [];
    const layerTabName = [];
    const xPointofLayers = [];
    layers = [...layers]
      .filter((lyr) => lyr.moving != true)
      .sort(function (a, b) {
        return a.x - b.x;
      });
    const errors = [];
    const warnings = [];
    let errorOccured = false;
    layers.forEach((layer: DrawingLayer) => {
      const rect = GetLayerRect(layer, xPointofLayers);
      if (!rect) {
        return;
      }
      const { layerRect, tabRect, hasError, error, hasWarning, warning } = rect;
      const layerNameDetail: TextProps = GetLayerName(
        tabRect.x,
        tabRect.y,
        tabRect.w,
        tabRect.h,
        layer.name,
        hasError && layer.isSaveAttempted ? 'white' : 'black',
      );

      _tabRects.push(tabRect);
      layer.layerRect = layerRect;
      layer.tabRect = GetUIRect(tabRect);
      layer.tabNameRect = GetUIText(layerNameDetail);
      try {
        if (
          (hasError || hasWarning) &&
          !isNullOrUndefined(errorLayer) &&
          !isNullOrUndefined(errorLayer.current) &&
          !isNullOrUndefined(errorLayerIconRef) &&
          !isNullOrUndefined(errorLayerIconRef.current)
        ) {
          if (hasError) {
            errorOccured = true;
          }
          if (!errors.find((e) => error.find((err) => err.error === e)) && hasError) {
            errors.push(...error);
          }
          if (
            !warnings.find((w) => warning.find((war) => war.warning === w)) &&
            hasWarning
          ) {
            warnings.push(...warning);
          }
          const src = hasError || errorOccured ? ErrorIcon : WarningIcon;
          const period = 150;

          const sizeNormalizer = strokeNormalizer;
          let errorLayerRef = null;
          errorLayerRef = errorLayerIconRef.current?.getLayer();
          const anim = new Konva.Animation((frame) => {
            if (
              !isNullOrUndefined(errorLayerIconRef) &&
              !isNullOrUndefined(errorLayerIconRef.current)
            ) {
              errorLayerIconRef.current?.opacity((Math.sin(frame.time / period) + 1) / 2);
            }
          }, errorLayerRef);
          setTimeout(function () {
            anim.stop();
            errorLayerIconRef.current?.batchDraw();
          }, 3000);
          anim.start();
          errorLayerIconRef.current?.batchDraw();
          let errorString = '';
          let errorIter = 1,
            warningIter = 1;
          errors.map((e) => {
            errorString += errorIter + '. ' + e.error + '\n';
            errorIter++;
          });
          let warningString = '';
          warnings.map((w) => {
            warningString += warningIter + '. ' + w.warning + '\n';
            warningIter++;
          });
          const tooltip = new Konva.Text({
            text: '',
            fontSize: 12 / sizeNormalizer,
            padding: 5,
            fill: hasError ? 'red' : 'gray',
            alpha: 0.75,
            x: 50,
            y: 30,
            visible: false,
            wrap: 'char',
          });
          const tooltipbackground = new Konva.Rect({
            x: 50,
            y: 30,
            stroke: hasError ? 'red' : 'gray',
            strokeWidth: 2 / strokeNormalizer,
            fill: 'white',
            width: tooltip.width() / sizeNormalizer,
            height: tooltip.height() / sizeNormalizer,
            shadowColor: 'black',
            visible: false,
          });

          tempErrorIcon = { src: src, msg: hasError ? errorString : warningString };
          errorLayer.current.add(tooltipbackground);
          errorLayer.current.add(tooltip);
        }
        setLayerErrorIcon(tempErrorIcon);
        if (
          !isNullOrUndefined(drawingLayer) &&
          drawingLayer.children.length > 0 &&
          hasError &&
          !isNullOrUndefined(_selectedLayer) &&
          _selectedLayer.id === layer.id &&
          layer.isSaveAttempted
        ) {
          layer.hasError = true;
          drawingLayer?.children[0]?.setAttrs({ stroke: 'red' });
          drawingLayer?.children[1]?.setAttrs({ stroke: 'red', fill: 'red' });
          drawingLayer?.children[2]?.setAttrs({ fill: 'white' });
          drawingLayer?.batchDraw();
        } else if (!isNullOrUndefined(_selectedLayer) && _selectedLayer.id === layer.id) {
          if (!isNullOrUndefined(drawingLayer) && drawingLayer.children.length > 0) {
            layer.hasError = false;
            drawingLayer.children[0]?.setAttrs({ stroke: selectionColor });
            drawingLayer.children[1]?.setAttrs({
              stroke: selectionColor,
              fill: selectionColor,
            });
            drawingLayer.children[2]?.setAttrs({
              fill: 'black',
              stroke: 'black',
            });
            drawingLayer.batchDraw();
          }
        }
      } catch (ex) {
        showSnackBar(ex);
      }
    });

    lRects.push(...layerTabName);
    return lRects;
  }

  function GetPlate(wellx, welly) {
    const maxGridX = exp.colCount - 1;
    const maxGridY = Math.floor(exp.plateDto.length / exp.colCount) - 1;
    const x = Math.min(Math.floor(wellx / exp.containerCols), maxGridX);
    const y = Math.min(Math.floor(welly / exp.containerRows), maxGridY);
    return exp.plateDto[x + y * exp.colCount];
  }

  function GetLayerName(
    X: number,
    Y: number,
    W: number,
    H: number,
    Name: string,
    color: string,
  ): TextProps {
    const layerNameDetail: TextProps = {
      x: X,
      y: Y,
      name: Name,
      size: H,
      w: W,
      h: H,
      color,
    };
    return layerNameDetail;
  }

  function GetLayerRect(layer, xPointofLayers) {
    let plate = GetPlate(layer.x, layer.y);
    let plateWellX = layer.x % exp.containerCols;
    let plateWellY = layer.y % exp.containerRows;
    const stWellRect = plate?.wellRects?.[plateWellX + plateWellY * exp.containerCols];

    if (stWellRect === undefined) {
      return null;
    }

    plate = GetPlate(layer.x + layer.width, layer.y + layer.height);
    plateWellX = (layer.x + layer.width) % exp.containerCols;
    plateWellY = (layer.y + layer.height) % exp.containerRows;
    const endWellRect = plate?.wellRects?.[plateWellX + plateWellY * exp.containerCols];

    if (endWellRect === undefined) {
      return null;
    }
    const wellNums = GetWellNum(
      layer.x % exp.containerCols,
      layer.y % exp.containerRows,
      layer.width,
      layer.height,
      exp.containerCols,
    );
    const errorDetails = findErrorsInLayer(exp, wellNums, layer);
    const layerRect: RMRectangle = {
      x: stWellRect.x,
      y: stWellRect.y,
      w: endWellRect.x + endWellRect.w - stWellRect.x,
      h: endWellRect.y + endWellRect.h - stWellRect.y,
      bColor:
        errorDetails.find((e) => e.hasError) && layer.isSaveAttempted
          ? layerErrorColor
          : layerColor,
    };
    layerRect.strokeWidth = 1.5 / strokeNormalizer;

    const isSubGrid =
      layer.type === LayerTypeDTO.Grid &&
      !isNullOrUndefined(layer.layerDetail) &&
      (layer.layerDetail as GridLayerDTO).subGridNumber > 1;

    const tabRect: RMRectangle = GetTabRect(
      stWellRect,
      xPointofLayers,
      layer.x,
      layer.y,
      errorDetails.find((e) => e.hasError) && layer.isSaveAttempted ? true : false,
      isSubGrid,
    );

    const filteredErrors = errorDetails.filter((e) => e.hasError);
    const filteredWarnings = errorDetails.filter((e) => e.hasWarning);
    return {
      layerRect,
      tabRect,
      hasError: filteredErrors.length > 0 ? true : false,
      error: filteredErrors,
      hasWarning: filteredWarnings.length > 0 ? true : false,
      warning: filteredWarnings,
    };
  }
  function GetTabRect(
    layerStartRect,
    xPointofLayers,
    layerX,
    layerY,
    hasError,
    isSubGrid = false,
  ) {
    // calculate tab rectangle too
    let { x } = layerStartRect;
    let { y } = layerStartRect;
    const w = isSubGrid
      ? Math.min(layerStartRect.w, layerStartRect.h) * 2
      : Math.min(layerStartRect.w, layerStartRect.h);
    const h = layerStartRect.h * 0.15;
    let calculatedTabX = 0,
      widthofPrevRect = 0,
      xOfPrevMostNearRect = 0;
    xPointofLayers.forEach((xPointofLayer) => {
      if (
        (layerX >= xPointofLayer[0] || layerX === xPointofLayer[0] - 1) &&
        layerY === xPointofLayer[1]
      ) {
        if (x <= xPointofLayer[2] + xPointofLayer[3]) {
          calculatedTabX += 1;
          widthofPrevRect = xPointofLayer[3];
          xOfPrevMostNearRect = xPointofLayer[2];
        }
      }
    });
    let newX = 0;
    if (calculatedTabX > 0 && xOfPrevMostNearRect + widthofPrevRect >= x) {
      newX = xOfPrevMostNearRect + 15; //widthofPrevRect;
    } else {
      newX = x;
      widthofPrevRect = w;
    }
    xPointofLayers.push([layerX, layerY, newX, widthofPrevRect]);
    y -= h * 0.75;
    x = newX;
    const tabRect: RMRectangle = {
      x,
      y,
      w,
      h,
      bColor: hasError ? layerErrorColor : layerColor,
    };
    tabRect.strokeWidth = 1.5 / strokeNormalizer;
    tabRect.fillColor = hasError ? layerErrorColor : layerColor;
    return tabRect;
  }

  function generateUILayers(layers: DrawingLayer[]): void {
    if (!layers) {
      return;
    }
    const groups = layers.map((layer, index) => {
      const rect = GetUIRect(layer.layerRect);

      return (
        <Group
          key={layer.id ?? index}
          id={`konva-group-design-layer-${layer.id ?? index}`}
        >
          {rect}
          {layer.tabRect}
          {layer.tabNameRect}
        </Group>
      );
    });

    setDrawableLayers(groups);
  }

  if (refresh) {
    setRefresh(false);
  }

  return (
    <Group id="exp-layersrenderer-group" visible={isDesignVisible()} listening={true}>
      {drawableLayers}
    </Group>
  );
};

export default LayerRenderer;
